import { useEffect, useState } from 'react';
import {
  Box, Paper, Stepper, Step, StepLabel, Button, Typography,
  Select, MenuItem, FormControl, InputLabel,
  Autocomplete, TextField, Alert, LinearProgress,
  FormControlLabel, Switch, Stack,
} from '@mui/material';
import FindInPageOutlinedIcon from '@mui/icons-material/FindInPageOutlined';
import { AWS_REGIONS } from '../../../constants/awsRegions';
import { enableMacie, getMacieConfig, resolveMacieMemberEmails } from '../../../services/macieApi';
import { listSecurityHubAccounts } from '../../../services/securityhubApi';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { POLL_INTERVAL_MS } from './pollInterval';
import MultiSelectField from './MultiSelectField';
import OperationProgress from './OperationProgress';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  CK, FONT_BODY, sectionCardSx, sectionHeadingSx, actionRowSx,
} from '../portalStyles';

const MACIE_COLOR = '#0d9488';
const MACIE_COLOR_DARK = '#0B7C72';
const steps = ['Admin account', 'Regions', 'Members', 'Emails', 'Discovery', 'Review'];
const EMAILS_STEP = 3;

// Macie-accented primary button (the theme default is the CSPM blue).
const macieButtonSx = {
  backgroundColor: MACIE_COLOR,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': { backgroundColor: MACIE_COLOR_DARK },
  '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
};

const quietButtonSx = {
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  color: CK.body,
  '&.Mui-disabled': { color: 'rgba(0, 0, 0, 0.26)' },
};

const bodyTextSx = { fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body };

// One row of the Review step — a fixed label column plus the chosen value, so
// all five rows share a single baseline grid.
function ReviewRow({ label, value }) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={{ xs: 0.25, sm: 2 }}
      sx={{ py: 0.5 }}
    >
      <Typography sx={{ minWidth: 160, fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.muted }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: FONT_BODY,
          fontSize: '0.875rem',
          fontWeight: 600,
          color: CK.navy,
          wordBreak: 'break-word',
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

export default function MacieSetup({ onComplete, priorFailure }) {
  const [active, setActive] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [adminAccountId, setAdminAccountId] = useState('');
  const [homeRegion, setHomeRegion] = useState('us-east-1');
  const [linkingRegions, setLinkingRegions] = useState(['us-east-1']);
  const [memberAccountIds, setMemberAccountIds] = useState([]);
  const [autoDiscovery, setAutoDiscovery] = useState(false);

  // Member-email resolution state (Emails step).
  const [emailRows, setEmailRows] = useState([]); // [{accountId, email, resolved, reason}]
  const [manualEmails, setManualEmails] = useState({}); // accountId -> typed root email
  const [resolvingEmails, setResolvingEmails] = useState(false);

  // In-progress state (after submit)
  const [config, setConfig] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const acctRes = await listSecurityHubAccounts();
        setAccounts(acctRes?.data?.accounts || acctRes?.data || []);
      } catch (e) {
        setError(e.message);
      }
    })();
  }, []);

  // Poll while in_progress.
  useEffect(() => {
    if (!config || config.status !== 'in_progress') return undefined;
    const id = setInterval(async () => {
      try {
        const res = await getMacieConfig();
        const next = res?.data;
        if (next) setConfig(next);
        if (next && next.status !== 'in_progress') {
          clearInterval(id);
          if (onComplete) onComplete();
        }
      } catch (_) { /* swallow transient */ }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [config, onComplete]);

  const next = () => setActive((s) => s + 1);
  const back = () => setActive((s) => s - 1);

  // Resolve member root emails when entering the Emails step or when the
  // member selection changes while on it. Auto-resolved accounts are read-only;
  // failures require a manual entry.
  useEffect(() => {
    if (active !== EMAILS_STEP) return;
    let cancelled = false;
    (async () => {
      setResolvingEmails(true);
      try {
        const res = await resolveMacieMemberEmails(memberAccountIds);
        const results = res?.data?.results || [];
        if (cancelled) return;
        setEmailRows(results);
        // Drop manual entries for accounts no longer selected / now auto-resolved.
        setManualEmails((cur) => {
          const next2 = {};
          results.forEach((row) => {
            if (!row.resolved && cur[row.accountId]) next2[row.accountId] = cur[row.accountId];
          });
          return next2;
        });
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setResolvingEmails(false);
      }
    })();
    return () => { cancelled = true; };
  }, [active, memberAccountIds]);

  const emailsResolved = emailRows.length === memberAccountIds.length
    && emailRows.every((row) => row.resolved || (manualEmails[row.accountId] || '').trim() !== '');

  const canNext = () => {
    if (active === 0) return !!adminAccountId;
    if (active === 1) return !!homeRegion && linkingRegions.length > 0 && linkingRegions.includes(homeRegion);
    if (active === EMAILS_STEP) return !resolvingEmails && emailsResolved;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      // Only send the emails the user actually typed; auto-resolved accounts
      // are re-resolved server-side.
      const memberEmails = {};
      Object.entries(manualEmails).forEach(([acctId, val]) => {
        const v = (val || '').trim();
        if (v) memberEmails[acctId] = v;
      });
      const res = await enableMacie({
        adminAccountId,
        homeRegion,
        linkingRegions,
        memberAccountIds,
        memberEmails,
        autoDiscovery,
      });
      setConfig(res?.data);
    } catch (e) {
      // A 422 means some accounts still need an email — send the user back to
      // the Emails step with the inline error.
      setError(e.message);
      setActive(EMAILS_STEP);
    } finally {
      setSubmitting(false);
    }
  };

  const body = () => {
    if (config) {
      const statusLabel = {
        in_progress: 'Setting up Macie…',
        enabled: 'Macie is enabled',
        partial_failure: 'Macie enabled with partial failures',
        failed: 'Setup failed',
        disabled: 'Macie is disabled',
      }[config.status] || config.status;
      return (
        <Box sx={{ py: 2 }}>
          <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>{statusLabel}</Typography>
          {config.status === 'in_progress' && (
            <>
              <Typography sx={{ ...bodyTextSx, mt: 1 }}>
                Enrolling member accounts, enabling Macie sessions, and inviting
                members from the administrator account. This can take a few minutes.
              </Typography>
              <OperationProgress
                percent={config.progress}
                label={config.progressLabel || 'Working…'}
                sx={{ my: 2 }}
              />
            </>
          )}
          {config.errorMessage && (
            <Alert severity="warning" sx={{ mt: 2 }}>{config.errorMessage}</Alert>
          )}
          <Box sx={{ mt: 2 }}>
            <ReviewRow label="Admin account" value={config.adminAccountId} />
            <ReviewRow label="Home region" value={config.homeRegion} />
          </Box>
        </Box>
      );
    }

    if (active === 0) {
      return (
        <Box sx={{ py: 2 }}>
          <Typography sx={{ ...bodyTextSx, mb: 2 }}>
            Pick one linked account to designate as the Macie administrator. This
            cannot be changed without tearing down and re-configuring.
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            The admin account is permanent for this deployment. To change it later
            you must disable Macie entirely and set it up again.
          </Alert>
          <Autocomplete
            options={accounts}
            getOptionLabel={(a) => `${a.accountName || a.name || a.accountId} (${a.accountId})`}
            value={accounts.find((a) => a.accountId === adminAccountId) || null}
            onChange={(_, v) => setAdminAccountId(v?.accountId || '')}
            renderInput={(params) => <TextField {...params} label="Admin account" />}
          />
        </Box>
      );
    }

    if (active === 1) {
      return (
        <Box sx={{ py: 2 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Home region</InputLabel>
            <Select
              value={homeRegion}
              label="Home region"
              onChange={(e) => {
                const v = e.target.value;
                setHomeRegion(v);
                if (!linkingRegions.includes(v)) setLinkingRegions([v, ...linkingRegions]);
              }}
            >
              {AWS_REGIONS.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
            </Select>
          </FormControl>
          <MultiSelectField
            label="Linking regions"
            options={AWS_REGIONS.map((r) => ({ value: r.value, label: r.label }))}
            value={linkingRegions}
            onChange={setLinkingRegions}
            lockedValues={homeRegion ? [homeRegion] : []}
          />
        </Box>
      );
    }

    if (active === 2) {
      const memberOptions = accounts.filter((a) => a.accountId !== adminAccountId);
      return (
        <Box sx={{ py: 2 }}>
          <Typography sx={{ ...bodyTextSx, mb: 2 }}>
            Pick the member accounts to enroll. The admin is always included.
          </Typography>
          <MultiSelectField
            label="Member accounts"
            options={memberOptions.map((a) => ({
              value: a.accountId,
              label: `${a.accountName || a.name || a.accountId} (${a.accountId})`,
            }))}
            value={memberAccountIds}
            onChange={setMemberAccountIds}
          />
        </Box>
      );
    }

    if (active === EMAILS_STEP) {
      return (
        <Box sx={{ py: 2 }}>
          <Typography sx={{ ...bodyTextSx, mb: 2 }}>
            Each member account needs its AWS root email to be invited. Emails are
            fetched automatically where possible; enter any that could not be resolved.
          </Typography>
          {resolvingEmails && <LinearProgress sx={{ mb: 2 }} />}
          {!resolvingEmails && emailRows.length === 0 && (
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.muted }}>
              No member accounts selected. Go back and pick at least one.
            </Typography>
          )}
          <Stack spacing={2}>
            {emailRows.map((row) => {
              const acct = accounts.find((a) => a.accountId === row.accountId);
              const acctLabel = acct
                ? `${acct.accountName || acct.name || row.accountId} (${row.accountId})`
                : row.accountId;
              return (
              <Stack
                key={row.accountId}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <Typography
                  sx={{
                    minWidth: { xs: 0, sm: 260 },
                    fontFamily: FONT_BODY,
                    fontSize: '0.875rem',
                    color: CK.navy,
                    wordBreak: 'break-word',
                  }}
                >
                  {acctLabel}
                </Typography>
                {row.resolved ? (
                  <Typography
                    sx={{
                      fontFamily: FONT_BODY,
                      fontSize: '0.875rem',
                      color: CK.green,
                      wordBreak: 'break-word',
                    }}
                  >
                    {row.email} (auto)
                  </Typography>
                ) : (
                  <TextField
                    size="small"
                    required
                    label="Root email"
                    sx={{ flex: 1, minWidth: 0 }}
                    value={manualEmails[row.accountId] || ''}
                    onChange={(e) => setManualEmails((m) => ({ ...m, [row.accountId]: e.target.value }))}
                    helperText={row.reason || 'Auto-fetch failed — enter this account’s AWS root email'}
                    error={!(manualEmails[row.accountId] || '').trim()}
                  />
                )}
              </Stack>
              );
            })}
          </Stack>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </Box>
      );
    }

    if (active === 4) {
      return (
        <Box sx={{ py: 2 }}>
          <Typography sx={{ ...bodyTextSx, mb: 2 }}>
            Automated sensitive-data discovery continuously samples objects across
            your enrolled S3 buckets and assigns each bucket a sensitivity score.
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Automated sensitive-data discovery is billed by AWS <b>per GB of S3 data
            inspected</b> across your entire organization. Leave it OFF to browse
            bucket posture and policy findings at no discovery cost. You can turn it
            on (or off) at any time later from Macie → Settings.
          </Alert>
          <FormControlLabel
            control={
              <Switch
                checked={autoDiscovery}
                onChange={(e) => setAutoDiscovery(e.target.checked)}
              />
            }
            label="Enable automated sensitive-data discovery (charged per GB inspected)"
          />
        </Box>
      );
    }

    // review
    return (
      <Box sx={{ py: 2 }}>
        <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Review</Typography>
        <ReviewRow label="Admin account" value={adminAccountId} />
        <ReviewRow label="Home region" value={homeRegion} />
        <ReviewRow label="Linking regions" value={linkingRegions.join(', ')} />
        <ReviewRow label="Member accounts" value={`${memberAccountIds.length + 1} (including admin)`} />
        <ReviewRow
          label="Automated discovery"
          value={autoDiscovery ? 'On (per-GB charges apply)' : 'Off'}
        />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Box>
    );
  };

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
        <PortalPageHeader
          icon={<FindInPageOutlinedIcon sx={{ fontSize: 24 }} />}
          title="Set up Macie"
          subtitle="Enable Amazon Macie S3 data-security posture across your organization"
          color={MACIE_COLOR}
        />
      </motion.div>
      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={sectionCardSx}>
        {priorFailure && !config && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            The previous setup attempt failed: {priorFailure}. Submitting again will clear that state and retry.
          </Alert>
        )}
        <Stepper
          activeStep={active}
          alternativeLabel
          sx={{
            mb: 3,
            overflowX: 'auto',
            pb: 1,
            '& .MuiStepLabel-label': { fontFamily: FONT_BODY, fontSize: '0.8rem', color: CK.body },
            '& .MuiStepLabel-label.Mui-active': { fontWeight: 600, color: CK.navy },
            '& .MuiStepLabel-label.Mui-completed': { fontWeight: 600, color: CK.navy },
            '& .MuiStepIcon-root.Mui-active': { color: MACIE_COLOR },
            '& .MuiStepIcon-root.Mui-completed': { color: MACIE_COLOR },
          }}
        >
          {steps.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
        </Stepper>
        {body()}
        {!config && (
          <Box sx={{ ...actionRowSx, justifyContent: 'flex-end', mt: 3 }}>
            <Button disabled={active === 0} onClick={back} sx={quietButtonSx}>Back</Button>
            {active < steps.length - 1 ? (
              <Button variant="contained" disabled={!canNext()} onClick={next} sx={macieButtonSx}>Next</Button>
            ) : (
              <Button variant="contained" disabled={submitting} onClick={submit} sx={macieButtonSx}>
                {submitting ? 'Enabling…' : 'Enable Macie'}
              </Button>
            )}
          </Box>
        )}
      </Paper>
      </motion.div>
    </motion.div>
  );
}
